import { ValidationError } from '~/errors';
import { urlWithQuery } from '~/utils/url';
import { LeasingBaseRepository } from './leasingBaseRepository';
import type * as Leasing from '~/swagger/leasing/Api';
import type { AnswerBatchPayload } from '~/utils/applicationAnswersBatch';

function validateId(id: string | undefined | null, entityName = 'RentalApplication'): asserts id is string {
  if (!id) throw new ValidationError(`${entityName} ID must be provided`);
}

export class LeasingRentalApplicationRepository extends LeasingBaseRepository {
  // ---- Applications ----

  public async list(filter?: Leasing.GetRentalApplicationsParams): Promise<Leasing.RentalApplicationListDtoPaginationDto> {
    const response = await this.fetchWithAuth(urlWithQuery(this.baseUrl('/rental-applications'), filter));
    return this.getJsonResponse(response);
  }

  public async get(applicationId: string): Promise<Leasing.RentalApplicationDto> {
    validateId(applicationId);
    const response = await this.fetchWithAuth(this.baseUrl(`/rental-applications/${applicationId}`));
    return this.getJsonResponse(response);
  }

  public async create(body: Leasing.CreateRentalApplicationDto): Promise<Leasing.RentalApplicationDto> {
    const response = await this.fetchWithAuth(this.baseUrl('/rental-applications'), {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return this.getJsonResponse(response);
  }

  public async update(applicationId: string, body: Leasing.UpdateRentalApplicationDto): Promise<Leasing.RentalApplicationDto> {
    validateId(applicationId);
    const response = await this.fetchWithAuth(this.baseUrl(`/rental-applications/${applicationId}`), {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    return this.getJsonResponse(response);
  }

  public async delete(applicationId: string): Promise<void> {
    validateId(applicationId);
    await this.fetchWithAuth(this.baseUrl(`/rental-applications/${applicationId}`), { method: 'DELETE' });
  }

  public async submit(applicationId: string): Promise<Leasing.SubmitRentalApplicationResult> {
    validateId(applicationId);
    const response = await this.fetchWithAuth(this.baseUrl(`/rental-applications/${applicationId}/submit`), { method: 'POST' });
    return this.getJsonResponse(response);
  }

  /** Generates the printable rental-application PDF and returns the stored file id + authenticated URL. */
  public async generatePdf(applicationId: string): Promise<{ fileId: string; secureUrl: string }> {
    validateId(applicationId);
    const response = await this.fetchWithAuth(this.baseUrl(`/rental-applications/${applicationId}/pdf`), { method: 'POST' });
    return this.getJsonResponse(response);
  }

  public async sendToOwner(
    applicationId: string,
    payload: { ownerIds: string[]; subject: string; body: string }
  ): Promise<{ fileId: string; sentOwnerIds: string[]; skippedOwnerIds: string[] }> {
    validateId(applicationId);
    const response = await this.fetchWithAuth(
      this.baseUrl(`/rental-applications/${applicationId}/send-to-owner`),
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      false
    );
    return this.getJsonResponse(response);
  }

  public async retract(applicationId: string): Promise<Leasing.RentalApplicationDto> {
    validateId(applicationId);
    const response = await this.fetchWithAuth(this.baseUrl(`/rental-applications/${applicationId}/retract`), { method: 'POST' });
    return this.getJsonResponse(response);
  }

  public async setDecision(applicationId: string, payload: Leasing.SetDecisionDto): Promise<Leasing.RentalApplicationDto> {
    validateId(applicationId);
    const response = await this.fetchWithAuth(this.baseUrl(`/rental-applications/${applicationId}/decision`), {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async requestInfoUpdate(applicationId: string, payload: Leasing.CreateInfoUpdateRequestDto = {}): Promise<void> {
    validateId(applicationId);
    await this.fetchWithAuth(this.baseUrl(`/rental-applications/${applicationId}/info-update-request`), {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  public async listInfoUpdateRequests(applicationId: string): Promise<Leasing.InfoUpdateRequestListDto> {
    validateId(applicationId);
    const response = await this.fetchWithAuth(this.baseUrl(`/rental-applications/${applicationId}/info-update-requests`));
    return this.getJsonResponse(response);
  }

  public async resendInfoUpdateRequest(applicationId: string, infoUpdateRequestId: string): Promise<Leasing.InfoUpdateRequestDto> {
    validateId(applicationId);
    validateId(infoUpdateRequestId, 'InfoUpdateRequest');
    const response = await this.fetchWithAuth(
      this.baseUrl(`/rental-applications/${applicationId}/info-update-requests/${infoUpdateRequestId}/resend`),
      { method: 'POST' },
      false
    );
    return this.getJsonResponse(response);
  }

  public async getGenieTake(applicationId: string): Promise<Leasing.GenieTakeDto> {
    validateId(applicationId);
    const response = await this.fetchWithAuth(this.baseUrl(`/rental-applications/${applicationId}/genie-take`));
    return this.getJsonResponse(response);
  }

  public async rescore(applicationId: string): Promise<Leasing.RentalApplicationDto> {
    validateId(applicationId);
    const response = await this.fetchWithAuth(this.baseUrl(`/rental-applications/${applicationId}/rescore`), { method: 'POST' }, false);
    return this.getJsonResponse(response);
  }

  public async getCommandCenter(): Promise<Leasing.CommandCenterDto> {
    const response = await this.fetchWithAuth(this.baseUrl('/rental-applications/command-center'));
    return this.getJsonResponse(response);
  }

  public async convertToTenant(applicationId: string): Promise<Leasing.ConvertedTenantDto> {
    validateId(applicationId);
    const response = await this.fetchWithAuth(this.baseUrl(`/rental-applications/${applicationId}/tenant-convert`), { method: 'POST' });
    return this.getJsonResponse(response);
  }

  // ---- Sub-resource CRUD helpers ----

  private subUrl(applicationId: string, resource: string, itemId?: string) {
    validateId(applicationId);
    const base = this.baseUrl(`/rental-applications/${applicationId}/${resource}`);
    return itemId ? `${base}/${itemId}` : base;
  }

  public async addSubResource<TItem, TPayload>(applicationId: string, resource: string, payload: TPayload): Promise<TItem> {
    const response = await this.fetchWithAuth(this.subUrl(applicationId, resource), {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async updateSubResource<TItem, TPayload>(
    applicationId: string,
    resource: string,
    itemId: string,
    payload: TPayload
  ): Promise<TItem> {
    validateId(itemId, `${resource} item`);
    const response = await this.fetchWithAuth(this.subUrl(applicationId, resource, itemId), {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async deleteSubResource(applicationId: string, resource: string, itemId: string): Promise<void> {
    validateId(itemId, `${resource} item`);
    await this.fetchWithAuth(this.subUrl(applicationId, resource, itemId), { method: 'DELETE' });
  }

  // ---- Residential history verification ----

  public async requestLandlordVerification(applicationId: string, residentialHistoryId: string): Promise<void> {
    validateId(residentialHistoryId, 'Residential history');
    await this.fetchWithAuth(
      this.baseUrl(`/rental-applications/${applicationId}/residential-history/${residentialHistoryId}/request-verification`),
      { method: 'POST' }
    );
  }

  public async resendLandlordVerification(applicationId: string, residentialHistoryId: string): Promise<void> {
    validateId(residentialHistoryId, 'Residential history');
    await this.fetchWithAuth(
      this.baseUrl(`/rental-applications/${applicationId}/residential-history/${residentialHistoryId}/resend-verification`),
      { method: 'POST' }
    );
  }

  // ---- Files (two-phase) ----

  public async createFileSession(
    applicationId: string,
    payload: Leasing.CreateFileUploadSessionDto
  ): Promise<Leasing.FileUploadSessionDto> {
    validateId(applicationId);
    const response = await this.fetchWithAuth(this.baseUrl(`/rental-applications/${applicationId}/files/session`), {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async commitFileSession(
    applicationId: string,
    sessionId: string,
    payload: Leasing.CommitFileUploadDto = {}
  ): Promise<Leasing.RentalApplicationFileDetailsDto> {
    validateId(applicationId);
    validateId(sessionId, 'File session');
    const response = await this.fetchWithAuth(this.baseUrl(`/rental-applications/${applicationId}/files/session/${sessionId}/commit`), {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async getFileDetails(applicationId: string, fileRowId: string): Promise<Leasing.RentalApplicationFileDetailsDto> {
    validateId(applicationId);
    validateId(fileRowId, 'File');
    const response = await this.fetchWithAuth(this.baseUrl(`/rental-applications/${applicationId}/files/${fileRowId}`));
    return this.getJsonResponse(response);
  }

  public async deleteFile(applicationId: string, fileId: string): Promise<void> {
    validateId(applicationId);
    validateId(fileId, 'File');
    await this.fetchWithAuth(this.baseUrl(`/rental-applications/${applicationId}/files/${fileId}`), { method: 'DELETE' });
  }

  // ---- Application questions (PM batch answer update) ----

  public async updateApplicationAnswers(applicationId: string, payload: AnswerBatchPayload): Promise<void> {
    validateId(applicationId);
    await this.fetchWithAuth(this.baseUrl(`/rental-applications/${applicationId}/application-questions`), {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  // ---- Screening + reports ----

  public async skipScreening(applicationId: string): Promise<void> {
    validateId(applicationId);
    await this.fetchWithAuth(this.baseUrl(`/rental-applications/${applicationId}/screening/skip`), { method: 'POST' });
  }

  public async requestApplicantCompleteScreening(applicationId: string): Promise<void> {
    validateId(applicationId);
    await this.fetchWithAuth(this.baseUrl(`/rental-applications/${applicationId}/screening/request-applicant-complete`), {
      method: 'POST',
    });
  }

  public async revalidateScreening(applicationId: string): Promise<Leasing.RentalApplicationScreeningDto> {
    validateId(applicationId);
    const response = await this.fetchWithAuth(
      this.baseUrl(`/rental-applications/${applicationId}/screening/revalidate`),
      { method: 'POST' },
      false
    );
    return this.getJsonResponse(response);
  }

  public async getReportCapabilities(applicationId: string): Promise<Leasing.RentalApplicationReportCapabilitiesDto> {
    validateId(applicationId);
    const response = await this.fetchWithAuth(this.baseUrl(`/rental-applications/${applicationId}/screening/report-capabilities`));
    return this.getJsonResponse(response);
  }

  public async getReports(applicationId: string): Promise<Leasing.RentalApplicationReportDto[]> {
    validateId(applicationId);
    const response = await this.fetchWithAuth(this.baseUrl(`/rental-applications/${applicationId}/screening/reports`));
    return this.getJsonResponse(response);
  }

  public async requestReports(applicationId: string, payload: Leasing.RequestReportsDto): Promise<Leasing.RentalApplicationReportDto[]> {
    validateId(applicationId);
    const response = await this.fetchWithAuth(this.baseUrl(`/rental-applications/${applicationId}/screening/reports/request`), {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async getReportHtml(applicationId: string, reportId: string): Promise<string> {
    validateId(applicationId);
    validateId(reportId, 'Report');
    const response = await this.fetchWithAuth(
      this.baseUrl(`/rental-applications/${applicationId}/screening/reports/${reportId}`),
      { headers: { Accept: 'text/html' } },
      false
    );
    return response.text();
  }

  // ---- Payment ----

  public async skipPayment(applicationId: string): Promise<void> {
    validateId(applicationId);
    await this.fetchWithAuth(this.baseUrl(`/rental-applications/${applicationId}/payment/skip`), { method: 'POST' });
  }

  public async remindApplicant(applicationId: string): Promise<void> {
    validateId(applicationId);
    await this.fetchWithAuth(this.baseUrl(`/rental-applications/${applicationId}/remind`), { method: 'POST' });
  }

  public async acceptBifurcatedScreening(applicationId: string): Promise<Leasing.RentalApplicationDto> {
    validateId(applicationId);
    const response = await this.fetchWithAuth(
      this.baseUrl(`/rental-applications/${applicationId}/bifurcated-screening/accept`),
      { method: 'POST' },
      false
    );
    return this.getJsonResponse(response) as Promise<Leasing.RentalApplicationDto>;
  }

  public async recordOfflinePayment(applicationId: string, payload: Leasing.RecordOfflinePaymentDto): Promise<void> {
    validateId(applicationId);
    await this.fetchWithAuth(this.baseUrl(`/rental-applications/${applicationId}/payment/offline`), {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  public async refundPayment(applicationId: string, payload: Leasing.RefundPaymentDto): Promise<void> {
    validateId(applicationId);
    await this.fetchWithAuth(this.baseUrl(`/rental-applications/${applicationId}/payment/refund`), {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // ---- Co-applicants ----

  public async inviteCoApplicant(applicationId: string, payload: Leasing.InviteCoApplicantDto): Promise<void> {
    validateId(applicationId);
    await this.fetchWithAuth(this.baseUrl(`/rental-applications/${applicationId}/co-applicants`), {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  public async removeCoApplicant(applicationId: string, coApplicantId: string): Promise<void> {
    validateId(applicationId);
    validateId(coApplicantId, 'Co-applicant');
    await this.fetchWithAuth(this.baseUrl(`/rental-applications/${applicationId}/co-applicants/${coApplicantId}`), {
      method: 'DELETE',
    });
  }

  // ---- Interests ----

  public async addInterest(
    applicationId: string,
    payload: Leasing.RentalApplicationInterestCreateDto
  ): Promise<Leasing.RentalApplicationInterestDto> {
    validateId(applicationId);
    const response = await this.fetchWithAuth(this.baseUrl(`/rental-applications/${applicationId}/interests`), {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async removeInterest(applicationId: string, interestId: string): Promise<void> {
    validateId(applicationId);
    validateId(interestId, 'Interest');
    await this.fetchWithAuth(this.baseUrl(`/rental-applications/${applicationId}/interests/${interestId}`), { method: 'DELETE' });
  }
}

export const leasingRentalApplicationRepo = new LeasingRentalApplicationRepository();
